import type { RouteObject } from "react-router";

import { Navigate, useRoutes } from "react-router";

import { ActivitiesList } from "@/features/Activities";
import Assets from "@/features/Assets";
import AssetTypes from "@/features/AssetTypes";
import Dashboard from "@/features/Dashboard";
import Import from "@/features/Import";
import Interventions from "@/features/Interventions";
import InterventionTemplates from "@/features/InterventionTemplates";
import Observations from "@/features/Observations";
import Organizations from "@/features/Organizations";
import Preferences from "@/features/Preferences";
import Projects from "@/features/Projects";
import Users from "@/features/Users";
import UserItems from "@/features/Users/Items";
import { ActivityLog } from "@/modules/activity/activity-log";
import { AppLayout } from "@/modules/app-layout";
import { AssetTypePage } from "@/modules/asset-type/asset-type-page";
import { AssetTypeTemplatePage } from "@/modules/asset-type-template/asset-type-template-page";
import { AssetTypeTemplatesListPage } from "@/modules/asset-type-templates/asset-type-templates-list-page";
import { LocationPage } from "@/modules/location/location-page";
import LocationsListPage from "@/modules/locations/locations-list-page";
import PageError from "@/modules/page-error";

import { AppRouteWrapper } from "./router-app-routes.component";
import { useAppMainRoutesLocation } from "./router-app-routes.hook";

const AppModalRoutesHandler = () => {
  return useRoutes(APP_MODAL_ROUTES);
};

const AppMainRoutesHandler = () => {
  // All Main routes can be displayed as normal pages or as background
  // pages. Background pages only exist with modal pages.
  const controlledLocation = useAppMainRoutesLocation();
  return useRoutes(APP_MAIN_ROUTES, controlledLocation);
};

export const APP_ROUTES: RouteObject[] = [
  {
    path: "/*",
    element: (
      <>
        {/* Keep order to make sure modal routes are working well */}
        <AppModalRoutesHandler />
        <AppMainRoutesHandler />
      </>
    ),
  },
];

const APP_MODAL_ROUTES: RouteObject[] = [
  {
    path: "observations/:id",
    element: (
      <AppRouteWrapper asModal modalParentPath="/observations">
        <Observations.Details />
      </AppRouteWrapper>
    ),
  },
];

const APP_MAIN_ROUTES: RouteObject[] = [
  {
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: (
          <AppRouteWrapper>
            <Dashboard />
          </AppRouteWrapper>
        ),
      },
      {
        path: "my-space",
        element: (
          <AppRouteWrapper>
            <UserItems />
          </AppRouteWrapper>
        ),
      },
      {
        path: "organizations",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <Organizations.List />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <Organizations.Details />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "locations",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <LocationsListPage />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <LocationPage />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "assets",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <Assets.List />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <Assets.Details />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "interventions",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <Interventions.List />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <Interventions.Details />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "observations",
        element: (
          <AppRouteWrapper>
            <Observations.List />
          </AppRouteWrapper>
        ),
      },
      {
        path: "projects",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <Projects.List />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <Projects.Details />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "import",
        element: (
          <AppRouteWrapper>
            <Import />
          </AppRouteWrapper>
        ),
      },
      {
        path: "users",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <Users.List />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <Users.Details />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "/templates/interventions/*",
        element: (
          <Navigate
            to={{
              pathname: location.pathname.replace(
                "/templates/interventions",
                "/intervention-templates"
              ),
              search: location.search,
            }}
            replace
          />
        ),
      },
      {
        path: "/templates/assets/*",
        element: (
          <Navigate
            to={{
              pathname: location.pathname.replace(
                "/templates/assets",
                "/asset-types"
              ),
              search: location.search,
            }}
            replace
          />
        ),
      },
      {
        path: "asset-types",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <AssetTypes.List />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <AssetTypePage />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "technical-forms",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <AssetTypeTemplatesListPage />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <AssetTypeTemplatePage />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "intervention-templates",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <InterventionTemplates.List />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <InterventionTemplates.Details />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "activities",
        children: [
          {
            index: true,
            element: (
              <AppRouteWrapper>
                <ActivitiesList />
              </AppRouteWrapper>
            ),
          },
          {
            path: ":id",
            element: (
              <AppRouteWrapper>
                <ActivityLog />
              </AppRouteWrapper>
            ),
          },
        ],
      },
      {
        path: "preferences",
        element: (
          <AppRouteWrapper>
            <Preferences />
          </AppRouteWrapper>
        ),
      },
      {
        path: "*",
        element: (
          <AppRouteWrapper>
            <PageError status={404} />
          </AppRouteWrapper>
        ),
      },
    ],
  },
];
